import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { httpClient } from '#/shared/api/http-client'
import {
  ADMIN_AUTH_LOGIN_PATH,
  ADMIN_AUTH_LOGOUT_PATH,
  ADMIN_AUTH_ME_PATH,
  ADMIN_AUTH_REFRESH_PATH,
} from '#/shared/api/paths'
import {
  clearSession,
  getRefreshToken,
  rotateStoredToken,
  storePendingSetup,
  storeSession,
  type AdminUser,
} from './session'

interface TokenPair {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

interface LoginResponse extends TokenPair {
  user: AdminUser
}

export interface SetupRequiredResponse {
  setup_required: true
  setup_token: string
  expires_in: number
  user: AdminUser
}

export interface LoginInput {
  email: string
  password: string
  remember: boolean
}

export const authKeys = {
  me: ['auth', 'me'] as const,
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      // `remember` is a client-side storage choice; the server has no use for it.
      const response = await httpClient.post<LoginResponse | SetupRequiredResponse>(
        ADMIN_AUTH_LOGIN_PATH,
        { email: input.email, password: input.password },
      )
      return response.data
    },
    onSuccess: (data, variables) => {
      // 202: the password is right, but the console opens only once a passkey
      // is added — the setup screen picks this up.
      if ('setup_required' in data) {
        storePendingSetup({
          setupToken: data.setup_token,
          email: data.user.email,
          reason: 'required',
          remember: variables.remember,
          expiresAt: Date.now() + data.expires_in * 1000,
        })
        return
      }
      storeSession(data.access_token, data.refresh_token, { remember: variables.remember })
      queryClient.setQueryData(authKeys.me, data.user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const refreshToken = getRefreshToken()
      if (refreshToken) {
        // A failed logout must not strand the user in a signed-in shell, so the
        // local session is cleared regardless of what the server says.
        await httpClient
          .post(ADMIN_AUTH_LOGOUT_PATH, { refresh_token: refreshToken })
          .catch(() => undefined)
      }
    },
    onSettled: () => {
      clearSession()
      queryClient.clear()
    },
  })
}

export function useCurrentAdmin() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: async (): Promise<AdminUser> => {
      const { data } = await httpClient.get<AdminUser>(ADMIN_AUTH_ME_PATH)
      return data
    },
    retry: false,
  })
}

/**
 * Swap the stored refresh token for a fresh pair.
 *
 * The server rotates on every call: the token presented is invalidated whether
 * or not the caller gets the reply. Two tabs starting together therefore race,
 * and the loser holds a token the server has already burned. When that happens
 * the winner has just written a new one to storage, so a single retry with
 * whatever is there now recovers the session instead of ending it.
 */
async function exchange(refreshToken: string): Promise<boolean> {
  const { data } = await httpClient.post<TokenPair>(ADMIN_AUTH_REFRESH_PATH, {
    refresh_token: refreshToken,
  })
  rotateStoredToken(data.access_token, data.refresh_token)
  return true
}

const RACE_GRACE_MS = 300

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function refreshSession(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    return await exchange(refreshToken)
  } catch {
    /* the token may have been rotated out from under us — see `exchange` */
  }

  // The winning tab writes its new token a moment after the server burns ours,
  // so a failure alone does not mean the session is over. Look again, once
  // immediately and once after the write has had time to land.
  for (const wait of [0, RACE_GRACE_MS]) {
    if (wait > 0) await sleep(wait)
    const rotated = getRefreshToken()
    if (rotated === null || rotated === refreshToken) continue
    try {
      return await exchange(rotated)
    } catch {
      break
    }
  }

  clearSession()
  return false
}

export async function restoreSession(): Promise<boolean> {
  return refreshSession()
}
